import React, { useState, useEffect } from "react";
import { Col, Row, Card, Form, Button, Alert, ProgressBar, Badge } from '@themesberg/react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faVideo, faTimes, faCheckCircle, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { collection, getDocs, doc, getDoc, addDoc, serverTimestamp, Timestamp } from 'firebase/firestore';
import { uploadToR2, generateUUID } from '../../utils/r2Upload';
import {  db } from '../../config/firebase';
import { useHistory } from 'react-router-dom';
import { Routes } from "../../routes";

export default () => {
    const [fakeAccounts, setFakeAccounts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const history = useHistory();

    // Toplu video state
    const [videoFiles, setVideoFiles] = useState([]); // [{file, thumbnail, thumbnailPreview, status, progress, error}]

    const [formData, setFormData] = useState({
        userId: "",
        description: "",
        durationValue: "",
        durationUnit: "hour",
        isPublic: false
    });

    useEffect(() => {
        const fetchAccounts = async () => {
            const q = collection(db, 'users');
            const snapshot = await getDocs(q);
            const accounts = [];
            snapshot.forEach(doc => {
                const data = doc.data();
                if (data.isTestUser === true || data.isFake === true) {
                    accounts.push({ id: doc.id, name: data.name || data.firstName || 'Bilinmeyen' });
                }
            });
            setFakeAccounts(accounts);
        };
        fetchAccounts();
    }, []);

    const generateThumbnail = (file) => {
        return new Promise((resolve) => {
            const video = document.createElement('video');
            video.preload = 'metadata';
            video.src = URL.createObjectURL(file);
            video.onloadedmetadata = () => {
                video.currentTime = 1;
            };
            video.onseeked = () => {
                const canvas = document.createElement('canvas');
                canvas.width = video.videoWidth;
                canvas.height = video.videoHeight;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
                canvas.toBlob((blob) => {
                    const thumbFile = new File([blob], "thumb.jpg", { type: "image/jpeg" });
                    resolve({
                        thumbnailFile: thumbFile,
                        thumbnailPreview: URL.createObjectURL(blob)
                    });
                }, 'image/jpeg', 0.7);
            };
            video.onerror = () => resolve({ thumbnailFile: null, thumbnailPreview: null });
        });
    };

    const handleFilesChange = async (e) => {
        const files = Array.from(e.target.files);
        if (files.length === 0) return;

        const newVideos = [];
        for (const file of files) {
            const thumb = await generateThumbnail(file);
            newVideos.push({
                file,
                thumbnailFile: thumb.thumbnailFile,
                thumbnailPreview: thumb.thumbnailPreview,
                status: 'pending', // pending, uploading, done, error
                progress: 0,
                error: null
            });
        }
        setVideoFiles(prev => [...prev, ...newVideos]);
    };

    const removeVideo = (index) => {
        setVideoFiles(prev => prev.filter((_, i) => i !== index));
    };

    const uploadSingleVideo = async (videoEntry, index, userData, expiresAt) => {
        // Update status to uploading
        setVideoFiles(prev => prev.map((v, i) => i === index ? { ...v, status: 'uploading', progress: 0 } : v));

        try {
            // 1. Upload video to R2
            const fileName = `reels/${formData.userId}/${Date.now()}_${index}_${videoEntry.file.name}`;
            const downloadURL = await uploadToR2(videoEntry.file, fileName, (progress) => {
                setVideoFiles(prev => prev.map((v, i) => i === index ? { ...v, progress } : v));
            });

            // 2. Upload thumbnail to R2
            let thumbnailURL = null;
            if (videoEntry.thumbnailFile) {
                const thumbName = `reels/${formData.userId}/${Date.now()}_${index}_thumb.jpg`;
                thumbnailURL = await uploadToR2(videoEntry.thumbnailFile, thumbName);
            }

            // 3. Save to Firestore (her video ayrı bir document)
            const reelData = {
                type: 'reel',
                userId: formData.userId,
                userName: `${userData.firstName || userData.name || ''} ${userData.lastName || ''}`.trim(),
                userProfileImageUrl: userData.profileImageUrl || null,
                videoUrl: downloadURL,
                thumbnailUrl: thumbnailURL,
                description: formData.description,
                city: userData.city || null,
                age: userData.age || null,
                createdAt: serverTimestamp(),
                expiresAt: expiresAt,
                isVisibleToOthers: true,
                isPublic: formData.isPublic,
                likeCount: 0,
                commentCount: 0,
                likedBy: []
            };

            await addDoc(collection(db, 'content'), reelData);

            // Success
            setVideoFiles(prev => prev.map((v, i) => i === index ? { ...v, status: 'done', progress: 100 } : v));
            return true;
        } catch (err) {
            console.error(`Error uploading video ${index}:`, err);
            setVideoFiles(prev => prev.map((v, i) => i === index ? { ...v, status: 'error', error: err.message } : v));
            return false;
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.userId || videoFiles.length === 0) {
            setError("Lütfen bir hesap ve en az bir video seçin.");
            return;
        }

        const pendingVideos = videoFiles.filter(v => v.status === 'pending' || v.status === 'error');
        if (pendingVideos.length === 0) {
            setError("Yüklenecek video yok. Tüm videolar zaten yüklendi.");
            return;
        }

        setLoading(true);
        setError(null);
        setSuccess(null);

        try {
            // Get user info (bir kere çek)
            const userDoc = await getDoc(doc(db, 'users', formData.userId));
            const userData = userDoc.data();

            // Calculate expiration
            let expiresAt = null;
            if (formData.durationValue) {
                const now = new Date();
                const value = parseInt(formData.durationValue);
                if (formData.durationUnit === "hour") {
                    now.setHours(now.getHours() + value);
                } else {
                    now.setDate(now.getDate() + value);
                }
                expiresAt = Timestamp.fromDate(now);
            }

            // Her videoyu sırayla yükle (Firebase Storage rate limit'e dikkat)
            let successCount = 0;
            let failCount = 0;

            for (let i = 0; i < videoFiles.length; i++) {
                if (videoFiles[i].status === 'done') continue; // Zaten yüklendi, atla
                const result = await uploadSingleVideo(videoFiles[i], i, userData, expiresAt);
                if (result) successCount++;
                else failCount++;
            }

            setLoading(false);

            if (failCount === 0) {
                setSuccess(`✅ ${successCount} video başarıyla paylaşıldı!`);
                setTimeout(() => {
                    history.push(Routes.ReelsList.path);
                }, 2000);
            } else {
                setSuccess(`${successCount} video başarılı, ${failCount} video hatalı. Hatalı videoları tekrar deneyebilirsiniz.`);
            }
        } catch (err) {
            console.error("Submit error:", err);
            setError("Hata oluştu: " + err.message);
            setLoading(false);
        }
    };

    const totalProgress = videoFiles.length > 0
        ? videoFiles.reduce((sum, v) => sum + (v.progress || 0), 0) / videoFiles.length
        : 0;

    return (
        <>
            <div className="py-4">
                <h4>Yeni Reels Ekle</h4>
                <p className="text-muted">Fake hesap adıyla Reels videosu paylaşın. Birden fazla video seçebilirsiniz.</p>
            </div>

            <Card border="light" className="shadow-sm mb-4">
                <Card.Body>
                    {error && <Alert variant="danger">{error}</Alert>}
                    {success && <Alert variant="success">{success}</Alert>}
                    <Form onSubmit={handleSubmit}>
                        <Row>
                            <Col md={6} className="mb-3">
                                <Form.Group id="userId">
                                    <Form.Label>Paylaşacak Hesap</Form.Label>
                                    <Form.Select required value={formData.userId} onChange={e => setFormData({ ...formData, userId: e.target.value })}>
                                        <option value="">Hesap Seçin...</option>
                                        {fakeAccounts.map(acc => (
                                            <option key={acc.id} value={acc.id}>{acc.name}</option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={6} className="mb-3">
                                <Form.Group id="video">
                                    <Form.Label>Video Dosyaları (MP4) — Birden fazla seçebilirsiniz</Form.Label>
                                    <Form.Control
                                        type="file"
                                        accept="video/mp4"
                                        multiple
                                        onChange={handleFilesChange}
                                        disabled={loading}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        {/* Seçili Videolar Listesi */}
                        {videoFiles.length > 0 && (
                            <div className="mb-3">
                                <Form.Label>Seçili Videolar ({videoFiles.length} adet)</Form.Label>
                                <div className="d-flex flex-wrap gap-3">
                                    {videoFiles.map((video, index) => (
                                        <div key={index} className="position-relative" style={{
                                            width: '120px',
                                            border: '2px solid',
                                            borderColor: video.status === 'done' ? '#28a745' : video.status === 'error' ? '#dc3545' : video.status === 'uploading' ? '#007bff' : '#6c757d',
                                            borderRadius: '8px',
                                            overflow: 'hidden',
                                            background: '#1a1a2e'
                                        }}>
                                            {video.thumbnailPreview ? (
                                                <img
                                                    src={video.thumbnailPreview}
                                                    alt={`Video ${index + 1}`}
                                                    style={{ width: '100%', height: '80px', objectFit: 'cover' }}
                                                />
                                            ) : (
                                                <div className="d-flex align-items-center justify-content-center" style={{ height: '80px', background: '#2a2a4a' }}>
                                                    <FontAwesomeIcon icon={faVideo} className="text-muted" />
                                                </div>
                                            )}

                                            {/* Status Badge */}
                                            <div className="text-center py-1" style={{ fontSize: '0.7rem' }}>
                                                {video.status === 'pending' && <Badge bg="secondary">Bekliyor</Badge>}
                                                {video.status === 'uploading' && (
                                                    <div>
                                                        <FontAwesomeIcon icon={faSpinner} spin className="text-primary" />
                                                        <div style={{ fontSize: '0.65rem' }}>%{Math.round(video.progress)}</div>
                                                    </div>
                                                )}
                                                {video.status === 'done' && <Badge bg="success"><FontAwesomeIcon icon={faCheckCircle} /> Tamam</Badge>}
                                                {video.status === 'error' && <Badge bg="danger">Hata</Badge>}
                                            </div>

                                            {/* Upload Progress */}
                                            {video.status === 'uploading' && (
                                                <ProgressBar
                                                    now={video.progress}
                                                    style={{ height: '3px', borderRadius: 0 }}
                                                    variant="primary"
                                                />
                                            )}

                                            {/* Remove Button (only when not uploading) */}
                                            {video.status !== 'uploading' && video.status !== 'done' && (
                                                <button
                                                    type="button"
                                                    onClick={() => removeVideo(index)}
                                                    className="position-absolute"
                                                    style={{
                                                        top: '2px', right: '2px',
                                                        background: 'rgba(220,53,69,0.9)',
                                                        border: 'none', borderRadius: '50%',
                                                        width: '20px', height: '20px',
                                                        color: '#fff', fontSize: '0.6rem',
                                                        cursor: 'pointer',
                                                        display: 'flex', alignItems: 'center', justifyContent: 'center'
                                                    }}
                                                >
                                                    <FontAwesomeIcon icon={faTimes} />
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        <Row>
                            <Col md={12} className="mb-3">
                                <Form.Group id="description">
                                    <Form.Label>Açıklama (tüm videolara uygulanır)</Form.Label>
                                    <Form.Control as="textarea" rows="3" placeholder="Video açıklaması..." value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })} />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={4} className="mb-3">
                                <Form.Group id="durationValue">
                                    <Form.Label>Yayın Süresi (Boşsa süresiz)</Form.Label>
                                    <Form.Control type="number" placeholder="Örn: 24" value={formData.durationValue} onChange={e => setFormData({ ...formData, durationValue: e.target.value })} />
                                </Form.Group>
                            </Col>
                            <Col md={2} className="mb-3">
                                <Form.Group id="durationUnit">
                                    <Form.Label>Birim</Form.Label>
                                    <Form.Select value={formData.durationUnit} onChange={e => setFormData({ ...formData, durationUnit: e.target.value })}>
                                        <option value="hour">Saat</option>
                                        <option value="day">Gün</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={6} className="mb-3 d-flex align-items-end">
                                <Form.Group id="isPublic" className="mb-2 w-100">
                                    <Form.Check
                                        type="switch"
                                        id="is-public"
                                        label="Herkese Açık Yap (Ör: Eğitmen vb Gold profiller için)"
                                        checked={formData.isPublic}
                                        onChange={(e) => setFormData({ ...formData, isPublic: e.target.checked })}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        {/* Toplam Progress */}
                        {loading && (
                            <div className="mb-3">
                                <p className="mb-1">
                                    Toplam İlerleme: %{Math.round(totalProgress)} —{' '}
                                    {videoFiles.filter(v => v.status === 'done').length}/{videoFiles.length} video tamamlandı
                                </p>
                                <ProgressBar now={totalProgress} label={`${Math.round(totalProgress)}%`} />
                            </div>
                        )}

                        <div className="mt-3">
                            <Button variant="primary" type="submit" disabled={loading || videoFiles.length === 0}>
                                <FontAwesomeIcon icon={faPlus} className="me-2" />
                                {loading
                                    ? `Yükleniyor (${videoFiles.filter(v => v.status === 'done').length}/${videoFiles.length})...`
                                    : `${videoFiles.length > 0 ? videoFiles.filter(v => v.status !== 'done').length : 0} Video Paylaş`
                                }
                            </Button>
                        </div>
                    </Form>
                </Card.Body>
            </Card >
        </>
    );
};
